import React, { useState, useCallback, useEffect } from 'react';
import { jsPDF } from 'jspdf';
import { reportsAPI, paymentsAPI, expendituresAPI, investmentExpendituresAPI } from '../services/api';
import { useYear } from '../context/YearContext';
import Modal from './Modal';

const fmt = (n) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(n || 0);

const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

const ddmmyyyy = (d) => {
  if (!d) return '—';
  const [y, m, dd] = String(d).slice(0, 10).split('-');
  return (y && m && dd) ? `${dd}/${m}/${y}` : String(d);
};

const today = () => new Date().toISOString().slice(0, 10);
const daysAgo = (n) => {
  const d = new Date(); d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
};
const startOfWeek = () => {
  const d = new Date(); d.setDate(d.getDate() - d.getDay() + 1);
  return d.toISOString().slice(0, 10);
};
const startOfMonth = () => {
  const d = new Date(); d.setDate(1);
  return d.toISOString().slice(0, 10);
};

const TYPE_LABEL = {
  monthly:  'Monthly',
  term:     'Term',
  old_due:  'Old Due',
  bus:      'Bus',
  uniform:  'Uniform',
  books:    'Books',
  other:    'Other',
};

const TYPE_COLOR = {
  monthly: '#1d4ed8', term: '#7c3aed', old_due: '#b45309',
  bus: '#0f766e', uniform: '#be185d', books: '#16a34a', other: '#6b7280',
};

export default function DayBook() {
  const { year } = useYear();
  const schoolName     = sessionStorage.getItem('gs_school_name')     || 'School';
  const schoolLocation = sessionStorage.getItem('gs_school_location') || '';
  const [activeTab, setActiveTab] = useState('collection');

  // ── Collection Register state ──
  const [fromDate,      setFromDate]      = useState(today());
  const [toDate,        setToDate]        = useState(today());
  const [rows,          setRows]          = useState([]);
  const [loading,       setLoading]       = useState(false);
  const [error,         setError]         = useState(null);
  const [fetched,       setFetched]       = useState(false);
  const [paymentFilter, setPaymentFilter] = useState('all'); // 'all' | 'cash' | 'online'
  const [cancellingReceipt, setCancellingReceipt] = useState(null); // receipt_no currently being cancelled

  // ── Expenditure Register state ──
  const [expFrom,     setExpFrom]     = useState(startOfMonth());
  const [expTo,       setExpTo]       = useState(today());
  const [expenses,    setExpenses]    = useState([]);
  const [expLoading,  setExpLoading]  = useState(false);
  const [expError,    setExpError]    = useState(null);
  const [expFetched,  setExpFetched]  = useState(false);
  const [showExpForm, setShowExpForm] = useState(false);
  const [expForm, setExpForm] = useState({
    expenditure_date: today(), description: '', amount: '', paid_to: '', notes: '', entry_type: 'debit', payment_mode: '',
  });

  // ── Expenditure edit state ──
  const [editingExp,    setEditingExp]    = useState(null);
  const [expEditForm,   setExpEditForm]   = useState({
    expenditure_date: today(), description: '', amount: '', paid_to: '', notes: '', entry_type: 'debit', payment_mode: '',
  });

  // ── Investment Expenditure state ──
  const [invFrom,      setInvFrom]      = useState(startOfMonth());
  const [invTo,        setInvTo]        = useState(today());
  const [invExpenses,  setInvExpenses]  = useState([]);
  const [invLoading,   setInvLoading]   = useState(false);
  const [invError,     setInvError]     = useState(null);
  const [invFetched,   setInvFetched]   = useState(false);
  const [showInvForm,  setShowInvForm]  = useState(false);
  const [invForm, setInvForm] = useState({
    expenditure_date: today(), description: '', amount: '', paid_to: '', payment_mode: '',
  });

  // ── Investment Expenditure edit state ──
  const [editingInv,   setEditingInv]   = useState(null);
  const [invEditForm,  setInvEditForm]  = useState({
    expenditure_date: today(), description: '', amount: '', paid_to: '', payment_mode: '',
  });

  // ── Collection Register logic ──
  const load = useCallback(async (from, to) => {
    setLoading(true);
    setError(null);
    try {
      const res = await reportsAPI.getDayBook({ from_date: from, to_date: to });
      setRows(res.data.data);
      setFetched(true);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load collection register');
    } finally {
      setLoading(false);
    }
  }, []);

  const setRange = (from, to) => {
    setFromDate(from); setToDate(to);
    load(from, to);
  };

  const handleCancelReceipt = async (receiptNo) => {
    if (!receiptNo) return;
    if (!window.confirm(
      `Cancel receipt ${receiptNo}?\n\nThis will completely erase the payment record everywhere and restore the fee balance to what it was before. This cannot be undone.`
    )) return;
    setCancellingReceipt(receiptNo);
    try {
      await paymentsAPI.deleteByReceipt(receiptNo);
      await load(fromDate, toDate);
      window.dispatchEvent(new CustomEvent('payment-recorded'));
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to cancel receipt.');
    } finally {
      setCancellingReceipt(null);
    }
  };

  // ── Expenditure Register logic ──
  const loadExpenses = useCallback(async (from, to) => {
    setExpLoading(true);
    setExpError(null);
    try {
      const res = await expendituresAPI.getAll({ from_date: from, to_date: to });
      setExpenses(res.data.data);
      setExpFetched(true);
    } catch (err) {
      setExpError(err.response?.data?.message || 'Failed to load expenditures');
    } finally {
      setExpLoading(false);
    }
  }, []);

  const handleExpSubmit = async (e) => {
    e.preventDefault();
    const { entry_type, amount, ...rest } = expForm;
    const payload = {
      ...rest,
      amount,
      debit:  entry_type === 'debit'  ? amount : '',
      credit: entry_type === 'credit' ? amount : '',
      academic_year: year,
    };
    try {
      await expendituresAPI.create(payload);
      setShowExpForm(false);
      setExpForm({ expenditure_date: today(), description: '', amount: '', paid_to: '', notes: '', entry_type: 'debit', payment_mode: '' });
      loadExpenses(expFrom, expTo);
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to add expenditure');
    }
  };

  const handleDeleteExp = async (id) => {
    if (!window.confirm('Delete this expenditure entry?')) return;
    try {
      await expendituresAPI.delete(id);
      loadExpenses(expFrom, expTo);
    } catch {
      alert('Failed to delete expenditure.');
    }
  };

  const openEditExp = (e) => {
    const entry_type = e.debit != null && parseFloat(e.debit) > 0 ? 'debit' : 'credit';
    setExpEditForm({
      expenditure_date: e.expenditure_date,
      description: e.description || '',
      amount: String(e.amount || ''),
      paid_to: e.paid_to || '',
      notes: e.notes || '',
      entry_type,
      payment_mode: e.payment_mode || '',
    });
    setEditingExp(e);
  };

  const handleExpEditSubmit = async (ev) => {
    ev.preventDefault();
    const { entry_type, amount, ...rest } = expEditForm;
    const payload = {
      ...rest,
      amount,
      debit:  entry_type === 'debit'  ? amount : '',
      credit: entry_type === 'credit' ? amount : '',
    };
    try {
      await expendituresAPI.update(editingExp.expenditure_id, payload);
      setEditingExp(null);
      loadExpenses(expFrom, expTo);
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to update expenditure');
    }
  };

  // ── Investment Expenditure logic ──
  const loadInvExpenses = useCallback(async (from, to) => {
    setInvLoading(true);
    setInvError(null);
    try {
      const res = await investmentExpendituresAPI.getAll({ from_date: from, to_date: to });
      setInvExpenses(res.data.data);
      setInvFetched(true);
    } catch (err) {
      setInvError(err.response?.data?.message || 'Failed to load investment expenditures');
    } finally {
      setInvLoading(false);
    }
  }, []);

  const handleInvSubmit = async (e) => {
    e.preventDefault();
    try {
      await investmentExpendituresAPI.create({ ...invForm, academic_year: year });
      setShowInvForm(false);
      setInvForm({ expenditure_date: today(), description: '', amount: '', paid_to: '', payment_mode: '' });
      loadInvExpenses(invFrom, invTo);
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to add investment expenditure');
    }
  };

  const handleDeleteInv = async (id) => {
    if (!window.confirm('Delete this investment expenditure entry?')) return;
    try {
      await investmentExpendituresAPI.delete(id);
      loadInvExpenses(invFrom, invTo);
    } catch {
      alert('Failed to delete investment expenditure.');
    }
  };

  const openEditInv = (e) => {
    setInvEditForm({
      expenditure_date: e.expenditure_date,
      description: e.description || '',
      amount: String(e.amount || ''),
      paid_to: e.paid_to || '',
      payment_mode: e.payment_mode || '',
    });
    setEditingInv(e);
  };

  const handleInvEditSubmit = async (ev) => {
    ev.preventDefault();
    try {
      await investmentExpendituresAPI.update(editingInv.investment_expenditure_id, invEditForm);
      setEditingInv(null);
      loadInvExpenses(invFrom, invTo);
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to update investment expenditure');
    }
  };

  const exportInvExpenditureCSV = () => {
    const headers = ['Date', 'Description', 'Amount', 'Payment Mode', 'Paid To'];
    const csvRows = [headers.join(',')];
    invExpenses.forEach((e) => {
      csvRows.push([
        e.expenditure_date,
        `"${(e.description || '').replace(/"/g, "'")}"`,
        e.amount,
        `"${(e.payment_mode || '').replace(/"/g, "'")}"`,
        `"${(e.paid_to || '').replace(/"/g, "'")}"`,
      ].join(','));
    });
    downloadCSV(csvRows.join('\n'), `Investment_Expenditure_${invFrom}_to_${invTo}.csv`);
  };

  // Auto-load expenditures when tab is opened
  useEffect(() => {
    if (activeTab === 'expenditure' && !expFetched) {
      loadExpenses(expFrom, expTo);
    }
  }, [activeTab, expFetched, expFrom, expTo, loadExpenses]);

  // Auto-load investment expenditures when tab is opened
  useEffect(() => {
    if (activeTab === 'investment' && !invFetched) {
      loadInvExpenses(invFrom, invTo);
    }
  }, [activeTab, invFetched, invFrom, invTo, loadInvExpenses]);

  // ── CSV export helpers ──
  const exportCollectionCSV = () => {
    const headers = ['Date', 'Receipt No', 'Student Name', 'Father Name', 'Class', 'Payment Type', 'Period/Term', 'Amount', 'Notes'];
    const csvRows = [headers.join(',')];
    rows.forEach((r) => {
      csvRows.push([
        ddmmyyyy(r.payment_date),
        `"${r.receipt_no || ''}"`,
        `"${r.student_name}"`,
        `"${r.father_name || ''}"`,
        `"${r.class}"`,
        TYPE_LABEL[r.payment_type] || r.payment_type,
        `"${r.month_year || r.term || ''}"`,
        r.amount,
        `"${(r.notes || '').replace(/"/g, "'")}"`,
      ].join(','));
    });
    downloadCSV(csvRows.join('\n'), `Collection_${fromDate}_to_${toDate}.csv`);
  };

  const exportExpenditureCSV = () => {
    const headers = ['Date', 'Description', 'Amount', 'Debit', 'Credit', 'Payment Mode', 'Paid To', 'Notes'];
    const csvRows = [headers.join(',')];
    expenses.forEach((e) => {
      csvRows.push([
        e.expenditure_date,
        `"${(e.description || '').replace(/"/g, "'")}"`,
        e.amount,
        e.debit  ?? '',
        e.credit ?? '',
        `"${(e.payment_mode || '').replace(/"/g, "'")}"`,
        `"${(e.paid_to || '').replace(/"/g, "'")}"`,
        `"${(e.notes || '').replace(/"/g, "'")}"`,
      ].join(','));
    });
    downloadCSV(csvRows.join('\n'), `Expenditure_${expFrom}_to_${expTo}.csv`);
  };

  const downloadCSV = (content, filename) => {
    const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  };

  // Apply payment mode filter
  const displayRows = paymentFilter === 'cash'
    ? rows.filter((r) => !r.is_online)
    : paymentFilter === 'online'
      ? rows.filter((r) => !!r.is_online)
      : rows;

  // Group rows by date
  const grouped = displayRows.reduce((acc, r) => {
    const key = r.payment_date;
    if (!acc[key]) acc[key] = [];
    acc[key].push(r);
    return acc;
  }, {});
  const dates = Object.keys(grouped).sort((a, b) => b.localeCompare(a));
  const grandTotal = displayRows.reduce((s, r) => s + parseFloat(r.amount || 0), 0);

  // Group expenses by date
  const expGrouped = expenses.reduce((acc, e) => {
    const key = e.expenditure_date;
    if (!acc[key]) acc[key] = [];
    acc[key].push(e);
    return acc;
  }, {});
  const expDates = Object.keys(expGrouped).sort((a, b) => b.localeCompare(a));
  const expGrandTotal = expenses.reduce((s, e) => s + parseFloat(e.amount || 0), 0);

  // Group investment expenses by date
  const invGrouped = invExpenses.reduce((acc, e) => {
    const key = e.expenditure_date;
    if (!acc[key]) acc[key] = [];
    acc[key].push(e);
    return acc;
  }, {});
  const invDates = Object.keys(invGrouped).sort((a, b) => b.localeCompare(a));
  const invGrandTotal = invExpenses.reduce((s, e) => s + parseFloat(e.amount || 0), 0);

  // ── Export PDF ──────────────────────────────────────────────────────────────
  const exportPDF = () => {
    const doc  = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'landscape' });
    const W    = doc.internal.pageSize.getWidth();
    const blue = [13, 36, 68];
    const grey = [102, 102, 102];
    const pageH = doc.internal.pageSize.getHeight();

    // Title header
    doc.setFillColor(...blue);
    doc.rect(0, 0, W, 18, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(13);
    doc.setTextColor(255, 255, 255);
    doc.text(`${schoolName.toUpperCase()} — COLLECTION REGISTER`, W / 2, 11, { align: 'center' });
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.text(`Period: ${fmtDate(fromDate)} to ${fmtDate(toDate)}   |   Total Collected: Rs.${grandTotal.toLocaleString('en-IN')}   |   ${rows.length} payments`, W / 2, 16, { align: 'center' });

    // Column widths
    const cols = [
      { label: 'DATE',         w: 25 },
      { label: 'RECEIPT NO',   w: 50 },
      { label: 'STUDENT',      w: 45 },
      { label: 'FATHER',       w: 40 },
      { label: 'CLASS',        w: 18 },
      { label: 'TYPE',         w: 22 },
      { label: 'PERIOD',       w: 25 },
      { label: 'AMOUNT',       w: 28 },
      { label: 'NOTES',        w: 35 },
    ];

    let y = 24;
    const rowH = 8;
    const startX = 8;

    const drawHeader = () => {
      doc.setFillColor(30, 58, 95);
      doc.rect(startX, y, cols.reduce((s, c) => s + c.w, 0), rowH, 'F');
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(8);
      doc.setTextColor(255, 255, 255);
      let x = startX + 2;
      cols.forEach((c) => { doc.text(c.label, x, y + 5.5); x += c.w; });
      y += rowH;
    };

    drawHeader();
    let rowIdx = 0;
    let currentDate = null;

    rows.slice().sort((a, b) => b.payment_date.localeCompare(a.payment_date)).forEach((r) => {
      if (y > pageH - 20) { doc.addPage(); y = 10; drawHeader(); }

      // Date group row
      if (r.payment_date !== currentDate) {
        currentDate = r.payment_date;
        const dayTotal = grouped[currentDate].reduce((s, p) => s + parseFloat(p.amount || 0), 0);
        doc.setFillColor(219, 234, 254);
        doc.rect(startX, y, cols.reduce((s, c) => s + c.w, 0), rowH - 1, 'F');
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(8);
        doc.setTextColor(30, 58, 95);
        doc.text(`${fmtDate(currentDate)}  (${grouped[currentDate].length} payments — Rs.${dayTotal.toLocaleString('en-IN')})`, startX + 2, y + 5.5);
        y += rowH - 1;
      }

      doc.setFillColor(rowIdx % 2 === 0 ? 255 : 248, rowIdx % 2 === 0 ? 255 : 250, rowIdx % 2 === 0 ? 255 : 255);
      doc.rect(startX, y, cols.reduce((s, c) => s + c.w, 0), rowH, 'F');
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7.5);
      doc.setTextColor(30, 30, 30);

      let x = startX + 2;
      const vals = [
        ddmmyyyy(r.payment_date),
        r.receipt_no || '—',
        r.student_name,
        r.father_name || '—',
        r.class,
        TYPE_LABEL[r.payment_type] || r.payment_type,
        r.month_year || r.term || '—',
        `Rs.${parseFloat(r.amount).toLocaleString('en-IN')}`,
        r.notes || '',
      ];
      cols.forEach((c, i) => {
        const raw = String(vals[i] || '');
        if (i === 1) {
          // Receipt No must never be cut — shrink font instead of truncating.
          doc.setFontSize(6.5);
          doc.text(raw.substring(0, 30), x, y + 5.5);
          doc.setFontSize(7.5);
        } else {
          const txt = raw.substring(0, Math.floor(c.w / 2.2));
          doc.text(txt, x, y + 5.5);
        }
        x += c.w;
      });
      y += rowH;
      rowIdx++;
    });

    // Grand total line
    if (y > pageH - 15) { doc.addPage(); y = 10; }
    doc.setFillColor(13, 36, 68);
    const totalW = cols.reduce((s, c) => s + c.w, 0);
    doc.rect(startX, y, totalW, rowH, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(255, 255, 255);
    doc.text(`TOTAL COLLECTED: Rs.${grandTotal.toLocaleString('en-IN')}  (${rows.length} payments)`, startX + 4, y + 5.5);
    y += rowH;

    doc.save(`DayBook_${fromDate}_to_${toDate}.pdf`);
  };

  const SHORTCUTS = [
    { label: 'Today',      from: today(),        to: today() },
    { label: 'Yesterday',  from: daysAgo(1),     to: daysAgo(1) },
    { label: 'This Week',  from: startOfWeek(),  to: today() },
    { label: 'This Month', from: startOfMonth(), to: today() },
    { label: 'Last 30d',   from: daysAgo(30),    to: today() },
  ];

  return (
    <div className="page">
      {/* ── Tab bar ── */}
      <div className="tabs">
        <button className={`tab${activeTab === 'collection' ? ' active' : ''}`} onClick={() => setActiveTab('collection')}>
          📒 Collection Register
        </button>
        <button className={`tab${activeTab === 'expenditure' ? ' active' : ''}`} onClick={() => { setActiveTab('expenditure'); setExpFetched(false); }}>
          💸 Expenditure
        </button>
        <button className={`tab${activeTab === 'investment' ? ' active' : ''}`} onClick={() => { setActiveTab('investment'); setInvFetched(false); }}>
          📈 Investment Expenditure
        </button>
      </div>

      {/* ════════════════════ COLLECTION REGISTER TAB ════════════════════ */}
      {activeTab === 'collection' && (
        <>
          <div className="page-header">
            <h1 className="page-title">📒 Collection Register</h1>
            <div style={{ display: 'flex', gap: 8 }}>
              {fetched && rows.length > 0 && (
                <>
                  <button className="btn btn-secondary" onClick={exportCollectionCSV}>📥 Export CSV</button>
                  <button className="btn btn-primary" onClick={exportPDF}>📄 Export PDF</button>
                </>
              )}
            </div>
          </div>

          {/* ── Filter bar ── */}
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', marginBottom: 16 }}>
            <label style={{ fontWeight: 600, fontSize: '0.9rem' }}>From</label>
            <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} lang="en-GB"
              style={{ padding: '6px 10px', border: '1px solid #ddd', borderRadius: 6 }} />
            <label style={{ fontWeight: 600, fontSize: '0.9rem' }}>To</label>
            <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} lang="en-GB"
              style={{ padding: '6px 10px', border: '1px solid #ddd', borderRadius: 6 }} />
            <button className="btn btn-primary" onClick={() => load(fromDate, toDate)} disabled={loading}>
              {loading ? 'Loading…' : '🔍 Search'}
            </button>
            <span style={{ color: '#999', fontSize: '0.8rem' }}>|</span>
            {SHORTCUTS.map((s) => (
              <button key={s.label}
                className="btn btn-secondary btn-sm"
                onClick={() => setRange(s.from, s.to)}
              >{s.label}</button>
            ))}
            <span style={{ color: '#999', fontSize: '0.8rem' }}>|</span>
            <select
              value={paymentFilter}
              onChange={(e) => setPaymentFilter(e.target.value)}
              style={{
                padding: '6px 10px', border: '1px solid #ddd', borderRadius: 6,
                fontSize: '0.85rem', background: '#fff', cursor: 'pointer',
                fontWeight: paymentFilter !== 'all' ? 600 : 400,
                borderColor: paymentFilter !== 'all' ? '#1d4ed8' : '#ddd',
                color: paymentFilter !== 'all' ? '#1d4ed8' : 'inherit',
              }}
            >
              <option value="all">All Payments</option>
              <option value="cash">Cash</option>
              <option value="online">Online (UPI / Card / Net Banking)</option>
            </select>
          </div>

          {error && <div className="error-banner">{error}</div>}

          {/* ── Summary Cards ── */}
          {fetched && !loading && (() => {
            const allCash   = rows.filter((r) => !r.is_online).reduce((s, r) => s + parseFloat(r.amount || 0), 0);
            const allOnline = rows.filter((r) => !!r.is_online).reduce((s, r) => s + parseFloat(r.amount || 0), 0);
            const allTotal  = rows.reduce((s, r) => s + parseFloat(r.amount || 0), 0);
            return (
              <div className="daybook-summary-grid">
                <div className="daybook-summary-card green">
                  <div className="dsc-label">Total Collected</div>
                  <div className="dsc-value">{fmt(allTotal)}</div>
                  <div className="dsc-sub">{rows.length} payments</div>
                </div>
                <div
                  className={`daybook-summary-card blue${paymentFilter === 'cash' ? ' dsc-active' : ''}`}
                  style={{ cursor: 'pointer' }}
                  onClick={() => setPaymentFilter(paymentFilter === 'cash' ? 'all' : 'cash')}
                  title="Click to filter Cash payments"
                >
                  <div className="dsc-label">Cash</div>
                  <div className="dsc-value">{fmt(allCash)}</div>
                  <div className="dsc-sub">in-person payments</div>
                </div>
                <div
                  className={`daybook-summary-card purple${paymentFilter === 'online' ? ' dsc-active' : ''}`}
                  style={{ cursor: 'pointer' }}
                  onClick={() => setPaymentFilter(paymentFilter === 'online' ? 'all' : 'online')}
                  title="Click to filter Online / UPI payments"
                >
                  <div className="dsc-label">Online / UPI</div>
                  <div className="dsc-value">{fmt(allOnline)}</div>
                  <div className="dsc-sub">digital payments</div>
                </div>
                <div className="daybook-summary-card orange">
                  <div className="dsc-label">Days</div>
                  <div className="dsc-value">{dates.length}</div>
                  <div className="dsc-sub">collection days</div>
                </div>
              </div>
            );
          })()}

          {/* ── Table ── */}
          {loading ? (
            <div className="loading">Loading…</div>
          ) : fetched && displayRows.length === 0 ? (
            <div className="no-data-cell" style={{ padding: 40, textAlign: 'center', color: '#999' }}>
              {rows.length === 0
                ? 'No payments found for the selected period.'
                : `No ${paymentFilter === 'cash' ? 'cash' : 'online'} payments found for the selected period.`}
            </div>
          ) : fetched ? (
            <div className="table-container" id="daybook-print">
              {dates.map((date) => {
                const dayRows  = grouped[date];
                const dayTotal = dayRows.reduce((s, r) => s + parseFloat(r.amount || 0), 0);
                return (
                  <div key={date} style={{ marginBottom: 24 }}>
                    <div style={{
                      background: '#1e3a5f', color: '#fff', padding: '8px 14px',
                      borderRadius: '8px 8px 0 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    }}>
                      <span style={{ fontWeight: 700 }}>{fmtDate(date)}</span>
                      <span style={{ fontSize: '0.85rem' }}>
                        {dayRows.length} payment{dayRows.length > 1 ? 's' : ''} — <strong>{fmt(dayTotal)}</strong>
                      </span>
                    </div>

                    <table className="data-table" style={{ borderRadius: '0 0 8px 8px', marginBottom: 0 }}>
                      <thead>
                        <tr>
                          <th>RECEIPT NO</th>
                          <th>STUDENT NAME</th>
                          <th>FATHER NAME</th>
                          <th>CLASS</th>
                          <th>PAYMENT TYPE</th>
                          <th>PERIOD / TERM</th>
                          <th>AMOUNT</th>
                          <th>NOTES</th>
                          <th>ACTION</th>
                        </tr>
                      </thead>
                      <tbody>
                        {dayRows.map((r) => (
                          <tr key={r.payment_id}>
                            <td>
                              <span style={{ fontFamily: 'monospace', fontSize: '0.8rem', color: '#1d4ed8', fontWeight: 600 }}>
                                {r.receipt_no || '—'}
                              </span>
                            </td>
                            <td><strong>{r.student_name}</strong></td>
                            <td>{r.father_name || '—'}</td>
                            <td><span className="class-badge">{r.class}</span></td>
                            <td>
                              <span style={{
                                display: 'inline-block', padding: '2px 8px', borderRadius: 12,
                                fontSize: '0.75rem', fontWeight: 600,
                                background: (TYPE_COLOR[r.payment_type] || '#6b7280') + '22',
                                color: TYPE_COLOR[r.payment_type] || '#6b7280',
                              }}>
                                {TYPE_LABEL[r.payment_type] || r.payment_type}
                              </span>
                            </td>
                            <td style={{ color: '#555', fontSize: '0.85rem' }}>
                              {r.month_year || r.term || '—'}
                            </td>
                            <td>
                              <strong style={{ color: '#16a34a' }}>{fmt(r.amount)}</strong>
                            </td>
                            <td style={{ color: '#888', fontSize: '0.8rem' }}>{r.notes || '—'}</td>
                            <td>
                              <button
                                type="button"
                                className="btn btn-danger"
                                style={{ padding: '4px 10px', fontSize: '0.75rem' }}
                                disabled={cancellingReceipt === r.receipt_no}
                                onClick={() => handleCancelReceipt(r.receipt_no)}
                              >
                                {cancellingReceipt === r.receipt_no ? 'Cancelling…' : 'Cancel'}
                              </button>
                            </td>
                          </tr>
                        ))}
                        <tr style={{ background: '#f0fdf4' }}>
                          <td colSpan={6} style={{ textAlign: 'right', fontWeight: 700, color: '#166534' }}>
                            Day Total
                          </td>
                          <td colSpan={3} style={{ fontWeight: 700, color: '#166534' }}>
                            {fmt(dayTotal)}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                );
              })}

              <div style={{
                display: 'flex', justifyContent: 'flex-end', alignItems: 'center',
                background: '#1e3a5f', color: '#fff', padding: '12px 20px',
                borderRadius: 8, marginTop: 8, gap: 16,
              }}>
                <span style={{ fontWeight: 600 }}>GRAND TOTAL ({displayRows.length} payments)</span>
                <span style={{ fontSize: '1.4rem', fontWeight: 700, color: '#fbbf24' }}>{fmt(grandTotal)}</span>
              </div>
            </div>
          ) : null}
        </>
      )}

      {/* ════════════════════ EXPENDITURE REGISTER TAB ════════════════════ */}
      {activeTab === 'expenditure' && (
        <>
          <div className="page-header">
            <h1 className="page-title">💸 Expenditure</h1>
            <div style={{ display: 'flex', gap: 8 }}>
              {expFetched && expenses.length > 0 && (
                <button className="btn btn-secondary" onClick={exportExpenditureCSV}>📥 Export CSV</button>
              )}
              <button className="btn btn-primary" onClick={() => setShowExpForm(true)}>
                + Add Expenditure
              </button>
            </div>
          </div>

          {/* ── Date filter ── */}
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', marginBottom: 16 }}>
            <label style={{ fontWeight: 600, fontSize: '0.9rem' }}>From</label>
            <input type="date" value={expFrom} onChange={(e) => setExpFrom(e.target.value)} lang="en-GB"
              style={{ padding: '6px 10px', border: '1px solid #ddd', borderRadius: 6 }} />
            <label style={{ fontWeight: 600, fontSize: '0.9rem' }}>To</label>
            <input type="date" value={expTo} onChange={(e) => setExpTo(e.target.value)} lang="en-GB"
              style={{ padding: '6px 10px', border: '1px solid #ddd', borderRadius: 6 }} />
            <button className="btn btn-primary" onClick={() => loadExpenses(expFrom, expTo)} disabled={expLoading}>
              {expLoading ? 'Loading…' : '🔍 Search'}
            </button>
          </div>

          {expError && <div className="error-banner">{expError}</div>}

          {/* ── Add Expenditure Modal ── */}
          {showExpForm && (
            <Modal onClose={() => setShowExpForm(false)}>
              <div className="modal-header">
                <div>
                  <h2>Add Expenditure</h2>
                  <div className="modal-subtitle">Record a new expense entry</div>
                </div>
                <button className="modal-close" onClick={() => setShowExpForm(false)}>✕</button>
              </div>
              <form onSubmit={handleExpSubmit}>
                <div className="modal-body">
                  <div className="form-group">
                    <label>Date <span className="required">*</span></label>
                    <input type="date" value={expForm.expenditure_date} required lang="en-GB"
                      onChange={(e) => setExpForm({ ...expForm, expenditure_date: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label>Description <span className="required">*</span></label>
                    <input type="text" value={expForm.description} placeholder="Brief description" required
                      onChange={(e) => setExpForm({ ...expForm, description: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label>Type <span className="required">*</span></label>
                    <div style={{ display: 'flex', gap: 0, borderRadius: 8, overflow: 'hidden', border: '1px solid #ddd' }}>
                      <button type="button"
                        onClick={() => setExpForm({ ...expForm, entry_type: 'debit' })}
                        style={{
                          flex: 1, padding: '10px 0', fontWeight: 700, fontSize: '0.9rem', cursor: 'pointer', border: 'none',
                          background: expForm.entry_type === 'debit' ? '#dc2626' : '#f9fafb',
                          color: expForm.entry_type === 'debit' ? '#fff' : '#374151',
                        }}>
                        Debit (Money Out)
                      </button>
                      <button type="button"
                        onClick={() => setExpForm({ ...expForm, entry_type: 'credit' })}
                        style={{
                          flex: 1, padding: '10px 0', fontWeight: 700, fontSize: '0.9rem', cursor: 'pointer', border: 'none',
                          borderLeft: '1px solid #ddd',
                          background: expForm.entry_type === 'credit' ? '#16a34a' : '#f9fafb',
                          color: expForm.entry_type === 'credit' ? '#fff' : '#374151',
                        }}>
                        Credit (Money In)
                      </button>
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Amount (₹) <span className="required">*</span></label>
                    <input type="number" value={expForm.amount} min="1" step="0.01" required placeholder="0"
                      onChange={(e) => setExpForm({ ...expForm, amount: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label>Paid To</label>
                    <input type="text" value={expForm.paid_to} placeholder="Name of person / vendor"
                      onChange={(e) => setExpForm({ ...expForm, paid_to: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label>Payment Mode</label>
                    <select value={expForm.payment_mode}
                      style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: '0.9rem', background: '#fff' }}
                      onChange={(e) => setExpForm({ ...expForm, payment_mode: e.target.value })}>
                      <option value="">— Select mode —</option>
                      <option value="Cash">Cash</option>
                      <option value="UPI">UPI</option>
                      <option value="Net Banking">Net Banking</option>
                      <option value="Credit Card">Credit Card</option>
                      <option value="Debit Card">Debit Card</option>
                      <option value="Others">Others</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Notes</label>
                    <textarea value={expForm.notes} placeholder="Any additional notes..." rows={3}
                      style={{ width: '100%', resize: 'vertical', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: '0.9rem' }}
                      onChange={(e) => setExpForm({ ...expForm, notes: e.target.value })} />
                  </div>
                </div>
                <div className="modal-actions">
                  <button type="button" className="btn btn-secondary" onClick={() => setShowExpForm(false)}>Cancel</button>
                  <button type="submit" className="btn btn-primary">💾 Save Expenditure</button>
                </div>
              </form>
            </Modal>
          )}

          {/* ── Edit Expenditure Modal ── */}
          {editingExp && (
            <Modal onClose={() => setEditingExp(null)}>
              <div className="modal-header">
                <div>
                  <h2>Edit Expenditure</h2>
                  <div className="modal-subtitle">Update this expense entry</div>
                </div>
                <button className="modal-close" onClick={() => setEditingExp(null)}>✕</button>
              </div>
              <form onSubmit={handleExpEditSubmit}>
                <div className="modal-body">
                  <div className="form-group">
                    <label>Date <span className="required">*</span></label>
                    <input type="date" value={expEditForm.expenditure_date} required lang="en-GB"
                      onChange={(e) => setExpEditForm({ ...expEditForm, expenditure_date: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label>Description <span className="required">*</span></label>
                    <input type="text" value={expEditForm.description} placeholder="Brief description" required
                      onChange={(e) => setExpEditForm({ ...expEditForm, description: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label>Type <span className="required">*</span></label>
                    <div style={{ display: 'flex', gap: 0, borderRadius: 8, overflow: 'hidden', border: '1px solid #ddd' }}>
                      <button type="button"
                        onClick={() => setExpEditForm({ ...expEditForm, entry_type: 'debit' })}
                        style={{
                          flex: 1, padding: '10px 0', fontWeight: 700, fontSize: '0.9rem', cursor: 'pointer', border: 'none',
                          background: expEditForm.entry_type === 'debit' ? '#dc2626' : '#f9fafb',
                          color: expEditForm.entry_type === 'debit' ? '#fff' : '#374151',
                        }}>
                        Debit (Money Out)
                      </button>
                      <button type="button"
                        onClick={() => setExpEditForm({ ...expEditForm, entry_type: 'credit' })}
                        style={{
                          flex: 1, padding: '10px 0', fontWeight: 700, fontSize: '0.9rem', cursor: 'pointer', border: 'none',
                          borderLeft: '1px solid #ddd',
                          background: expEditForm.entry_type === 'credit' ? '#16a34a' : '#f9fafb',
                          color: expEditForm.entry_type === 'credit' ? '#fff' : '#374151',
                        }}>
                        Credit (Money In)
                      </button>
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Amount (₹) <span className="required">*</span></label>
                    <input type="number" value={expEditForm.amount} min="1" step="0.01" required placeholder="0"
                      onChange={(e) => setExpEditForm({ ...expEditForm, amount: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label>Paid To</label>
                    <input type="text" value={expEditForm.paid_to} placeholder="Name of person / vendor"
                      onChange={(e) => setExpEditForm({ ...expEditForm, paid_to: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label>Payment Mode</label>
                    <select value={expEditForm.payment_mode}
                      style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: '0.9rem', background: '#fff' }}
                      onChange={(e) => setExpEditForm({ ...expEditForm, payment_mode: e.target.value })}>
                      <option value="">— Select mode —</option>
                      <option value="Cash">Cash</option>
                      <option value="UPI">UPI</option>
                      <option value="Net Banking">Net Banking</option>
                      <option value="Credit Card">Credit Card</option>
                      <option value="Debit Card">Debit Card</option>
                      <option value="Others">Others</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Notes</label>
                    <textarea value={expEditForm.notes} placeholder="Any additional notes..." rows={3}
                      style={{ width: '100%', resize: 'vertical', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: '0.9rem' }}
                      onChange={(e) => setExpEditForm({ ...expEditForm, notes: e.target.value })} />
                  </div>
                </div>
                <div className="modal-actions">
                  <button type="button" className="btn btn-secondary" onClick={() => setEditingExp(null)}>Cancel</button>
                  <button type="submit" className="btn btn-primary">💾 Save Changes</button>
                </div>
              </form>
            </Modal>
          )}

          {/* ── Summary chips ── */}
          {expFetched && !expLoading && (() => {
            const totalDebit  = expenses.reduce((s, e) => s + parseFloat(e.debit  || 0), 0);
            const totalCredit = expenses.reduce((s, e) => s + parseFloat(e.credit || 0), 0);
            const netBalance  = totalCredit - totalDebit;
            return (
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
                <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '8px 16px' }}>
                  <div style={{ fontSize: '0.75rem', color: '#dc2626', fontWeight: 600 }}>TOTAL SPENT</div>
                  <div style={{ fontSize: '1.4rem', fontWeight: 700, color: '#dc2626' }}>{fmt(expGrandTotal)}</div>
                </div>
                <div style={{ background: '#fefce8', border: '1px solid #fde68a', borderRadius: 8, padding: '8px 16px' }}>
                  <div style={{ fontSize: '0.75rem', color: '#b45309', fontWeight: 600 }}>ENTRIES</div>
                  <div style={{ fontSize: '1.4rem', fontWeight: 700, color: '#b45309' }}>{expenses.length}</div>
                </div>
                <div style={{ background: '#f5f3ff', border: '1px solid #ddd6fe', borderRadius: 8, padding: '8px 16px' }}>
                  <div style={{ fontSize: '0.75rem', color: '#7c3aed', fontWeight: 600 }}>DAYS</div>
                  <div style={{ fontSize: '1.4rem', fontWeight: 700, color: '#7c3aed' }}>{expDates.length}</div>
                </div>
                {(totalDebit > 0 || totalCredit > 0) && (
                  <>
                    <div style={{ background: '#fff1f2', border: '1px solid #fecdd3', borderRadius: 8, padding: '8px 16px' }}>
                      <div style={{ fontSize: '0.75rem', color: '#dc2626', fontWeight: 600 }}>TOTAL DEBIT</div>
                      <div style={{ fontSize: '1.4rem', fontWeight: 700, color: '#dc2626' }}>{fmt(totalDebit)}</div>
                    </div>
                    <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: '8px 16px' }}>
                      <div style={{ fontSize: '0.75rem', color: '#16a34a', fontWeight: 600 }}>TOTAL CREDIT</div>
                      <div style={{ fontSize: '1.4rem', fontWeight: 700, color: '#16a34a' }}>{fmt(totalCredit)}</div>
                    </div>
                    <div style={{ background: netBalance >= 0 ? '#f0fdf4' : '#fef2f2', border: `1px solid ${netBalance >= 0 ? '#bbf7d0' : '#fecaca'}`, borderRadius: 8, padding: '8px 16px' }}>
                      <div style={{ fontSize: '0.75rem', color: netBalance >= 0 ? '#16a34a' : '#dc2626', fontWeight: 600 }}>NET BALANCE</div>
                      <div style={{ fontSize: '1.4rem', fontWeight: 700, color: netBalance >= 0 ? '#16a34a' : '#dc2626' }}>{fmt(netBalance)}</div>
                    </div>
                  </>
                )}
              </div>
            );
          })()}

          {/* ── Expenditure Table ── */}
          {expLoading ? (
            <div className="loading">Loading…</div>
          ) : expFetched && expenses.length === 0 ? (
            <div className="no-data-cell" style={{ padding: 40, textAlign: 'center', color: '#999' }}>
              No expenditures found for the selected period.
            </div>
          ) : expFetched ? (
            <div className="table-container">
              {expDates.map((date) => {
                const dayExps  = expGrouped[date];
                const dayTotal = dayExps.reduce((s, e) => s + parseFloat(e.amount || 0), 0);
                return (
                  <div key={date} style={{ marginBottom: 24 }}>
                    <div style={{
                      background: '#7c3aed', color: '#fff', padding: '8px 14px',
                      borderRadius: '8px 8px 0 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    }}>
                      <span style={{ fontWeight: 700 }}>{fmtDate(date)}</span>
                      <span style={{ fontSize: '0.85rem' }}>
                        {dayExps.length} entr{dayExps.length > 1 ? 'ies' : 'y'} — <strong>{fmt(dayTotal)}</strong>
                      </span>
                    </div>

                    <table className="data-table" style={{ borderRadius: '0 0 8px 8px', marginBottom: 0 }}>
                      <thead>
                        <tr>
                          <th>DESCRIPTION</th>
                          <th>PAID TO</th>
                          <th>AMOUNT</th>
                          <th style={{ color: '#dc2626' }}>DEBIT</th>
                          <th style={{ color: '#16a34a' }}>CREDIT</th>
                          <th>BALANCE</th>
                          <th>PAYMENT MODE</th>
                          <th>ACTIONS</th>
                          <th>NOTES</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(() => {
                          let running = 0;
                          return dayExps.map((e) => {
                            const dr = parseFloat(e.debit  || 0);
                            const cr = parseFloat(e.credit || 0);
                            running += cr - dr;
                            return (
                              <tr key={e.expenditure_id}>
                                <td>{e.description || '—'}</td>
                                <td>{e.paid_to || '—'}</td>
                                <td><strong style={{ color: '#dc2626' }}>{fmt(e.amount)}</strong></td>
                                <td style={{ color: '#dc2626', fontWeight: 600 }}>{e.debit  != null ? fmt(e.debit)  : '—'}</td>
                                <td style={{ color: '#16a34a', fontWeight: 600 }}>{e.credit != null ? fmt(e.credit) : '—'}</td>
                                <td>
                                  <span style={{ fontWeight: 700, color: running >= 0 ? '#16a34a' : '#dc2626' }}>
                                    {fmt(running)}
                                  </span>
                                </td>
                                <td>
                                  {e.payment_mode ? (
                                    <span style={{
                                      display: 'inline-block', padding: '2px 8px', borderRadius: 12,
                                      fontSize: '0.75rem', fontWeight: 600,
                                      background: '#f3f4f6', color: '#374151', border: '1px solid #e5e7eb',
                                    }}>{e.payment_mode}</span>
                                  ) : '—'}
                                </td>
                                <td style={{ display: 'flex', gap: 6, flexWrap: 'nowrap' }}>
                                  <button className="btn btn-sm btn-secondary"
                                    onClick={() => openEditExp(e)}
                                    title="Edit">✏️</button>
                                  <button className="btn btn-sm btn-danger"
                                    onClick={() => handleDeleteExp(e.expenditure_id)}
                                    title="Delete">🗑️</button>
                                </td>
                                <td style={{ color: '#6b7280', fontSize: '0.82rem', maxWidth: 200 }}>{e.notes || '—'}</td>
                              </tr>
                            );
                          });
                        })()}
                        <tr style={{ background: '#fef2f2' }}>
                          <td colSpan={2} style={{ textAlign: 'right', fontWeight: 700, color: '#991b1b' }}>Day Total</td>
                          <td style={{ fontWeight: 700, color: '#991b1b' }}>{fmt(dayTotal)}</td>
                          <td style={{ fontWeight: 700, color: '#dc2626' }}>{fmt(dayExps.reduce((s, e) => s + parseFloat(e.debit  || 0), 0))}</td>
                          <td style={{ fontWeight: 700, color: '#16a34a' }}>{fmt(dayExps.reduce((s, e) => s + parseFloat(e.credit || 0), 0))}</td>
                          <td colSpan={4} style={{ fontWeight: 700, color: (() => { const n = dayExps.reduce((s,e)=>s+parseFloat(e.credit||0)-parseFloat(e.debit||0),0); return n>=0?'#16a34a':'#dc2626'; })() }}>
                            {fmt(dayExps.reduce((s,e)=>s+parseFloat(e.credit||0)-parseFloat(e.debit||0),0))}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                );
              })}

              {(() => {
                const totalDebit  = expenses.reduce((s, e) => s + parseFloat(e.debit  || 0), 0);
                const totalCredit = expenses.reduce((s, e) => s + parseFloat(e.credit || 0), 0);
                const netBalance  = totalCredit - totalDebit;
                return (
                  <div style={{
                    display: 'flex', justifyContent: 'flex-end', alignItems: 'center', flexWrap: 'wrap',
                    background: '#7c3aed', color: '#fff', padding: '12px 20px',
                    borderRadius: 8, marginTop: 8, gap: 20,
                  }}>
                    <span style={{ fontWeight: 600 }}>GRAND TOTAL ({expenses.length} entries)</span>
                    <span style={{ fontSize: '1.2rem', fontWeight: 700, color: '#fbbf24' }}>{fmt(expGrandTotal)}</span>
                    {(totalDebit > 0 || totalCredit > 0) && (
                      <>
                        <span style={{ opacity: 0.6, fontSize: '0.85rem' }}>|</span>
                        <span style={{ fontSize: '0.9rem' }}>DR: <strong style={{ color: '#fca5a5' }}>{fmt(totalDebit)}</strong></span>
                        <span style={{ fontSize: '0.9rem' }}>CR: <strong style={{ color: '#86efac' }}>{fmt(totalCredit)}</strong></span>
                        <span style={{ fontSize: '0.9rem' }}>
                          Net: <strong style={{ color: netBalance >= 0 ? '#86efac' : '#fca5a5', fontSize: '1.1rem' }}>{fmt(netBalance)}</strong>
                        </span>
                      </>
                    )}
                  </div>
                );
              })()}
            </div>
          ) : null}

        </>
      )}

      {/* ════════════════════ INVESTMENT EXPENDITURE TAB ════════════════════ */}
      {activeTab === 'investment' && (
        <>
          <div className="page-header">
            <h1 className="page-title">📈 Investment Expenditure</h1>
            <div style={{ display: 'flex', gap: 8 }}>
              {invFetched && invExpenses.length > 0 && (
                <button className="btn btn-secondary" onClick={exportInvExpenditureCSV}>📥 Export CSV</button>
              )}
              <button className="btn btn-primary" onClick={() => setShowInvForm(true)}>
                + Add Investment Expenditure
              </button>
            </div>
          </div>

          {/* ── Date filter ── */}
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', marginBottom: 16 }}>
            <label style={{ fontWeight: 600, fontSize: '0.9rem' }}>From</label>
            <input type="date" value={invFrom} onChange={(e) => setInvFrom(e.target.value)} lang="en-GB"
              style={{ padding: '6px 10px', border: '1px solid #ddd', borderRadius: 6 }} />
            <label style={{ fontWeight: 600, fontSize: '0.9rem' }}>To</label>
            <input type="date" value={invTo} onChange={(e) => setInvTo(e.target.value)} lang="en-GB"
              style={{ padding: '6px 10px', border: '1px solid #ddd', borderRadius: 6 }} />
            <button className="btn btn-primary" onClick={() => loadInvExpenses(invFrom, invTo)} disabled={invLoading}>
              {invLoading ? 'Loading…' : '🔍 Search'}
            </button>
          </div>

          {invError && <div className="error-banner">{invError}</div>}

          {/* ── Add Investment Expenditure Modal ── */}
          {showInvForm && (
            <Modal onClose={() => setShowInvForm(false)}>
              <div className="modal-header">
                <div>
                  <h2>Add Investment Expenditure</h2>
                  <div className="modal-subtitle">Record a new investment expense entry</div>
                </div>
                <button className="modal-close" onClick={() => setShowInvForm(false)}>✕</button>
              </div>
              <form onSubmit={handleInvSubmit}>
                <div className="modal-body">
                  <div className="form-group">
                    <label>Date <span className="required">*</span></label>
                    <input type="date" value={invForm.expenditure_date} required lang="en-GB"
                      onChange={(e) => setInvForm({ ...invForm, expenditure_date: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label>Description <span className="required">*</span></label>
                    <input type="text" value={invForm.description} placeholder="Brief description" required
                      onChange={(e) => setInvForm({ ...invForm, description: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label>Amount (₹) <span className="required">*</span></label>
                    <input type="number" value={invForm.amount} min="1" step="0.01" required placeholder="0"
                      onChange={(e) => setInvForm({ ...invForm, amount: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label>Paid To</label>
                    <input type="text" value={invForm.paid_to} placeholder="Name of person / vendor"
                      onChange={(e) => setInvForm({ ...invForm, paid_to: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label>Payment Mode</label>
                    <select value={invForm.payment_mode}
                      style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: '0.9rem', background: '#fff' }}
                      onChange={(e) => setInvForm({ ...invForm, payment_mode: e.target.value })}>
                      <option value="">— Select mode —</option>
                      <option value="Cash">Cash</option>
                      <option value="UPI">UPI</option>
                      <option value="Net Banking">Net Banking</option>
                      <option value="Credit Card">Credit Card</option>
                      <option value="Debit Card">Debit Card</option>
                      <option value="Others">Others</option>
                    </select>
                  </div>
                </div>
                <div className="modal-actions">
                  <button type="button" className="btn btn-secondary" onClick={() => setShowInvForm(false)}>Cancel</button>
                  <button type="submit" className="btn btn-primary">💾 Save Investment Expenditure</button>
                </div>
              </form>
            </Modal>
          )}

          {/* ── Edit Investment Expenditure Modal ── */}
          {editingInv && (
            <Modal onClose={() => setEditingInv(null)}>
              <div className="modal-header">
                <div>
                  <h2>Edit Investment Expenditure</h2>
                  <div className="modal-subtitle">Update this investment expense entry</div>
                </div>
                <button className="modal-close" onClick={() => setEditingInv(null)}>✕</button>
              </div>
              <form onSubmit={handleInvEditSubmit}>
                <div className="modal-body">
                  <div className="form-group">
                    <label>Date <span className="required">*</span></label>
                    <input type="date" value={invEditForm.expenditure_date} required lang="en-GB"
                      onChange={(e) => setInvEditForm({ ...invEditForm, expenditure_date: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label>Description <span className="required">*</span></label>
                    <input type="text" value={invEditForm.description} placeholder="Brief description" required
                      onChange={(e) => setInvEditForm({ ...invEditForm, description: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label>Amount (₹) <span className="required">*</span></label>
                    <input type="number" value={invEditForm.amount} min="1" step="0.01" required placeholder="0"
                      onChange={(e) => setInvEditForm({ ...invEditForm, amount: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label>Paid To</label>
                    <input type="text" value={invEditForm.paid_to} placeholder="Name of person / vendor"
                      onChange={(e) => setInvEditForm({ ...invEditForm, paid_to: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label>Payment Mode</label>
                    <select value={invEditForm.payment_mode}
                      style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: '0.9rem', background: '#fff' }}
                      onChange={(e) => setInvEditForm({ ...invEditForm, payment_mode: e.target.value })}>
                      <option value="">— Select mode —</option>
                      <option value="Cash">Cash</option>
                      <option value="UPI">UPI</option>
                      <option value="Net Banking">Net Banking</option>
                      <option value="Credit Card">Credit Card</option>
                      <option value="Debit Card">Debit Card</option>
                      <option value="Others">Others</option>
                    </select>
                  </div>
                </div>
                <div className="modal-actions">
                  <button type="button" className="btn btn-secondary" onClick={() => setEditingInv(null)}>Cancel</button>
                  <button type="submit" className="btn btn-primary">💾 Save Changes</button>
                </div>
              </form>
            </Modal>
          )}

          {/* ── Summary chips ── */}
          {invFetched && !invLoading && (
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
              <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '8px 16px' }}>
                <div style={{ fontSize: '0.75rem', color: '#dc2626', fontWeight: 600 }}>TOTAL INVESTED</div>
                <div style={{ fontSize: '1.4rem', fontWeight: 700, color: '#dc2626' }}>{fmt(invGrandTotal)}</div>
              </div>
              <div style={{ background: '#fefce8', border: '1px solid #fde68a', borderRadius: 8, padding: '8px 16px' }}>
                <div style={{ fontSize: '0.75rem', color: '#b45309', fontWeight: 600 }}>ENTRIES</div>
                <div style={{ fontSize: '1.4rem', fontWeight: 700, color: '#b45309' }}>{invExpenses.length}</div>
              </div>
              <div style={{ background: '#f5f3ff', border: '1px solid #ddd6fe', borderRadius: 8, padding: '8px 16px' }}>
                <div style={{ fontSize: '0.75rem', color: '#7c3aed', fontWeight: 600 }}>DAYS</div>
                <div style={{ fontSize: '1.4rem', fontWeight: 700, color: '#7c3aed' }}>{invDates.length}</div>
              </div>
            </div>
          )}

          {/* ── Investment Expenditure Table ── */}
          {invLoading ? (
            <div className="loading">Loading…</div>
          ) : invFetched && invExpenses.length === 0 ? (
            <div className="no-data-cell" style={{ padding: 40, textAlign: 'center', color: '#999' }}>
              No investment expenditures found for the selected period.
            </div>
          ) : invFetched ? (
            <div className="table-container">
              {invDates.map((date) => {
                const dayExps  = invGrouped[date];
                const dayTotal = dayExps.reduce((s, e) => s + parseFloat(e.amount || 0), 0);
                return (
                  <div key={date} style={{ marginBottom: 24 }}>
                    <div style={{
                      background: '#0369a1', color: '#fff', padding: '8px 14px',
                      borderRadius: '8px 8px 0 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    }}>
                      <span style={{ fontWeight: 700 }}>{fmtDate(date)}</span>
                      <span style={{ fontSize: '0.85rem' }}>
                        {dayExps.length} entr{dayExps.length > 1 ? 'ies' : 'y'} — <strong>{fmt(dayTotal)}</strong>
                      </span>
                    </div>

                    <table className="data-table" style={{ borderRadius: '0 0 8px 8px', marginBottom: 0 }}>
                      <thead>
                        <tr>
                          <th>DESCRIPTION</th>
                          <th>PAID TO</th>
                          <th>AMOUNT</th>
                          <th>PAYMENT MODE</th>
                          <th>ACTIONS</th>
                        </tr>
                      </thead>
                      <tbody>
                        {dayExps.map((e) => (
                          <tr key={e.investment_expenditure_id}>
                            <td>{e.description || '—'}</td>
                            <td>{e.paid_to || '—'}</td>
                            <td><strong style={{ color: '#0369a1' }}>{fmt(e.amount)}</strong></td>
                            <td>
                              {e.payment_mode ? (
                                <span style={{
                                  display: 'inline-block', padding: '2px 8px', borderRadius: 12,
                                  fontSize: '0.75rem', fontWeight: 600,
                                  background: '#f3f4f6', color: '#374151', border: '1px solid #e5e7eb',
                                }}>{e.payment_mode}</span>
                              ) : '—'}
                            </td>
                            <td style={{ display: 'flex', gap: 6, flexWrap: 'nowrap' }}>
                              <button className="btn btn-sm btn-secondary"
                                onClick={() => openEditInv(e)}
                                title="Edit">✏️</button>
                              <button className="btn btn-sm btn-danger"
                                onClick={() => handleDeleteInv(e.investment_expenditure_id)}
                                title="Delete">🗑️</button>
                            </td>
                          </tr>
                        ))}
                        <tr style={{ background: '#e0f2fe' }}>
                          <td colSpan={2} style={{ textAlign: 'right', fontWeight: 700, color: '#0369a1' }}>Day Total</td>
                          <td colSpan={3} style={{ fontWeight: 700, color: '#0369a1' }}>{fmt(dayTotal)}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                );
              })}

              <div style={{
                display: 'flex', justifyContent: 'flex-end', alignItems: 'center',
                background: '#0369a1', color: '#fff', padding: '12px 20px',
                borderRadius: 8, marginTop: 8, gap: 16,
              }}>
                <span style={{ fontWeight: 600 }}>GRAND TOTAL ({invExpenses.length} entries)</span>
                <span style={{ fontSize: '1.4rem', fontWeight: 700, color: '#fbbf24' }}>{fmt(invGrandTotal)}</span>
              </div>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}
